import React, { useEffect, useRef } from 'react';
import { Modal, Button, Row, Col, Card, Spin, Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import moment from 'moment';
import 'moment/locale/fr';
import { useReactToPrint } from 'react-to-print';
import { useCheckIsAnneeInactive } from 'app/hooks/useCheckIsAnneeActive';

moment.locale('fr');

const ClasseCalendar = ({
  events,
  loadingCalendar,
  classe,
  activeAnneScolaire,
  isNotComptable,
  isNotSurveillantGeneral,
  isProf,
  isAdmin,
  visibleModal,
  visibleResetModal,
  setVisibleModal,
  setVisibleResetModal,
  handleModal,
  handleEventDrop,
  renderEventContent,
  resetCalendar,
  calendarEntryId,
  onDelete,
  anneeScolaireId,
}) => {
  const inactiveAnnee = useCheckIsAnneeInactive(anneeScolaireId);

  const componentRef = useRef();
  const calendarRef = useRef<FullCalendar>();

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `Emploi du Temps - ${classe.nom} ${activeAnneScolaire.nom}`,
  });

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();
        calendarApi.updateSize();
      }
    });

    if (componentRef.current) {
      resizeObserver.observe(componentRef.current);
    }

    return () => {
      if (componentRef.current) {
        resizeObserver.unobserve(componentRef.current);
      }
    };
  }, []);
  return (
    <>
      <Modal
        title="Confirmation de la suppression"
        centered
        footer={[
          <Button key="back" onClick={() => setVisibleModal(false)}>
            Annuler
          </Button>,
          <Button key="submit" type="primary" danger onClick={onDelete}>
            Supprimer
          </Button>,
        ]}
        visible={visibleModal}
        closable={false}
        className="custom-modal"
      >
        Cette action supprimera également toutes les données liées à ce cours. Êtes-vous certain de vouloir continuer ?
      </Modal>
      <Modal
        title="Confirmation de la réinitialisation"
        centered
        footer={[
          <Button key="back" onClick={() => setVisibleResetModal(false)}>
            Annuler
          </Button>,
          <Button key="submit" type="primary" danger onClick={() => resetCalendar(classe.id)}>
            Confirmer
          </Button>,
        ]}
        visible={visibleResetModal}
        closable={false}
        className="custom-modal"
      >
        Êtes-vous certain de vouloir réinitialiser l’emploi du temps de la classe {classe.nom} ?
      </Modal>

      <Row justify="space-between" gutter={[16, 16]} className="calendar-controls" align="middle">
        <Col xs={24} sm={12} md={16} style={{ display: 'flex', justifyContent: 'flex-start' }}>
          <div className="button-group">
            {isNotComptable && isNotSurveillantGeneral && !isProf && (
              <Button disabled={inactiveAnnee} type="primary" className="action-btn add-btn" onClick={handleModal}>
                <FontAwesomeIcon icon="plus" /> Ajouter un cours
              </Button>
            )}
            {isAdmin && (
              <Button
                disabled={inactiveAnnee}
                type="primary"
                danger
                className="action-btn reset-btn"
                onClick={() => setVisibleResetModal(true)}
              >
                <FontAwesomeIcon icon={faTrashAlt} /> Réinitialiser
              </Button>
            )}
          </div>
        </Col>

        <Col xs={24} sm={12} md={8} style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="default" className="action-btn print-btn" onClick={handlePrint}>
            <FontAwesomeIcon icon={faPrint} /> Imprimer
          </Button>
        </Col>
      </Row>

      <div className="calendar-card" ref={componentRef}>
        <h3 className="calendar-title">Emploi du Temps - {classe.nom}</h3>
        <Spin spinning={loadingCalendar} tip="Chargement..." className="custom-spin">
          <FullCalendar
            ref={calendarRef}
            plugins={[timeGridPlugin, interactionPlugin]}
            initialView="timeGridWeek"
            events={events}
            editable={isNotComptable && isNotSurveillantGeneral && !isProf}
            eventDrop={handleEventDrop}
            eventContent={renderEventContent}
            slotMinTime="07:00:00"
            slotMaxTime="20:00:00"
            slotDuration="00:30:00"
            allDaySlot={false}
            height="auto"
            headerToolbar={{
              left: '',
              center: 'title',
              right: '',
            }}
            initialDate={moment().startOf('isoWeek').format('YYYY-MM-DD')}
            hiddenDays={[0]}
            slotLabelFormat={{ hour: 'numeric', minute: '2-digit', hour12: false }}
            eventTimeFormat={{ hour: 'numeric', minute: '2-digit', hour12: false }}
            weekends={true}
            eventOverlap={false}
            slotEventOverlap={false}
            locale="fr"
            titleFormat={() => `${activeAnneScolaire.nom}`}
            dayHeaderContent={info => (
              <span style={{ color: '#1d3557' }}>{moment(info.date).locale('fr').format('dddd').toUpperCase()}</span>
            )}
            dateClick={handleModal}
          />
        </Spin>
      </div>
    </>
  );
};

export default ClasseCalendar;
